import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from '../api/api.service';

export type OfferApprovalLevel = {
    level: number;
    policy: 'any' | 'all';
    approver_user_ids: string[];
};

export type OfferApprovalMember = {
    id: string;
    name: string | null;
    email: string | null;
    role_code: string | null;
};

export type OfferApprovalWorkflow = {
    /** Approvals are opt-in per org; false means offers send with no approval cycle. */
    enabled: boolean;
    /** Upper bound the API enforces. A workflow may have anywhere from 1 to this. */
    maxLevels: number;
    levels: OfferApprovalLevel[] | null;
    members: OfferApprovalMember[];
};

@Injectable({ providedIn: 'root' })
export class OfferApprovalsService {
    private api = inject(ApiService);

    getWorkflow(): Observable<OfferApprovalWorkflow> {
        return this.api.get<any>('/API2/offer-approval-workflow').pipe(
            map((response) => response?.data || response || {}),
            map((data) => ({
                enabled: Boolean(data?.enabled),
                maxLevels: Number(data?.max_levels) > 0 ? Number(data.max_levels) : 3,
                levels: Array.isArray(data?.levels) ? data.levels : null,
                members: Array.isArray(data?.members) ? data.members : []
            }))
        );
    }

    /**
     * `enabled` is omitted rather than sent as false when undefined — the API
     * treats an absent flag as "leave the switch alone", so saving levels can
     * never silently turn approvals on or off.
     */
    saveWorkflow(levels: OfferApprovalLevel[], enabled?: boolean): Observable<any> {
        const body: { levels: OfferApprovalLevel[]; enabled?: boolean } = { levels };
        if (enabled !== undefined) body.enabled = enabled;
        return this.api.put<any>('/API2/offer-approval-workflow', body);
    }

    list(status = 'pending'): Observable<any[]> {
        return this.api.get<any>('/API2/offer-approvals', new HttpParams().set('status', status)).pipe(
            map((response) => Array.isArray(response?.data) ? response.data : [])
        );
    }

    submit(offerId: string): Observable<any> {
        return this.api.post<any>(`/API2/offers/${encodeURIComponent(offerId)}/approval-submit`, {});
    }

    decide(requestId: string, decision: 'approved' | 'rejected', comment?: string | null): Observable<any> {
        return this.api.post<any>(`/API2/offer-approvals/${encodeURIComponent(requestId)}/decision`, { decision, comment: comment || null });
    }
}
